import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { money } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, AlertTriangle } from "lucide-react";

const EMPTY = { name: "", unit: "kg", stock: 0, reorder_level: 5, cost_per_unit: 0, wastage: 0 };

export default function Inventory() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const { data: items = [] } = useQuery({ queryKey: ["inventory"], queryFn: async () => (await api.get("/inventory")).data });
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
  const sym = settings?.currency_symbol || "₹";

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (i) => { setEditing(i); setForm(i); setOpen(true); };
  const save = async () => {
    if (!form.name) return toast.error("Name required");
    const body = { name: form.name, unit: form.unit, stock: Number(form.stock), reorder_level: Number(form.reorder_level), cost_per_unit: Number(form.cost_per_unit), wastage: Number(form.wastage) };
    if (editing) await api.put(`/inventory/${editing.id}`, body); else await api.post("/inventory", body);
    toast.success("Saved"); setOpen(false); qc.invalidateQueries({ queryKey: ["inventory"] });
  };
  const del = async (i) => { await api.delete(`/inventory/${i.id}`); toast.success("Deleted"); qc.invalidateQueries({ queryKey: ["inventory"] }); };

  const lowCount = items.filter((i) => i.stock <= i.reorder_level).length;

  return (
    <div className="h-full overflow-y-auto p-6 lg:p-8">
      <PageHeader title="Inventory" subtitle={`${items.length} ingredients · ${lowCount} low on stock`}>
        <Button onClick={openNew} data-testid="add-inv-btn" className="bg-[var(--kul-primary)] hover:bg-[var(--kul-primary-hover)] text-white"><Plus className="w-4 h-4 mr-1" /> Add Item</Button>
      </PageHeader>

      <div className="kul-card overflow-hidden">
        <Table>
          <TableHeader><TableRow className="bg-neutral-50">
            <TableHead>Item</TableHead><TableHead>Stock</TableHead><TableHead>Reorder At</TableHead>
            <TableHead className="text-right">Cost/Unit</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {items.map((i, idx) => {
              const low = i.stock <= i.reorder_level;
              return (
                <TableRow key={i.id} className={idx % 2 ? "bg-neutral-50/50" : ""} data-testid={`inv-row-${i.id}`}>
                  <TableCell className="font-medium">{i.name}</TableCell>
                  <TableCell className="font-mono-k">{i.stock} {i.unit}</TableCell>
                  <TableCell className="font-mono-k text-neutral-500">{i.reorder_level} {i.unit}</TableCell>
                  <TableCell className="text-right font-mono-k">{money(i.cost_per_unit, sym)}</TableCell>
                  <TableCell>{low ? <Badge className="bg-red-100 text-red-700 gap-1"><AlertTriangle className="w-3 h-3" />Low</Badge> : <Badge className="bg-green-100 text-green-700">OK</Badge>}</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(i)} data-testid={`inv-edit-${i.id}`}><Pencil className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" className="text-[var(--kul-danger)]" onClick={() => del(i)} data-testid={`inv-del-${i.id}`}><Trash2 className="w-4 h-4" /></Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent><DialogHeader><DialogTitle>{editing ? "Edit" : "Add"} Ingredient</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="inv-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Unit</Label><Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder="kg / ltr / pcs" data-testid="inv-unit" /></div>
              <div><Label>Stock</Label><Input type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} data-testid="inv-stock" /></div>
              <div><Label>Reorder Level</Label><Input type="number" value={form.reorder_level} onChange={(e) => setForm({ ...form, reorder_level: e.target.value })} data-testid="inv-reorder" /></div>
              <div><Label>Cost / Unit</Label><Input type="number" value={form.cost_per_unit} onChange={(e) => setForm({ ...form, cost_per_unit: e.target.value })} data-testid="inv-cost" /></div>
            </div>
          </div>
          <DialogFooter><Button onClick={save} className="bg-[var(--kul-primary)] text-white" data-testid="save-inv">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
